/**
 * Shared source of truth for the styled-system → Tailwind shim.
 *
 * Both `resolveStyledProps` (runtime, emits classes/inline styles) and the
 * `system-props-utilities.css` generator read from this module, so the classes
 * the resolver can emit and the CSS rules Seeds ships can never drift.
 */

/** `typeScale` is a styled-system `variant` reusing the fontSize scale — see
 * resolveStyledProps.ts's dedicated handling. */
export const TYPE_SCALE_PROP_NAME = "typeScale" as const;

/**
 * Seeds space scale keys (from @sproutsocial/seeds-space). These map 1:1 to the
 * Tailwind `--spacing-*` keys the theme preset registers, e.g. `mt-300`.
 */
export const SPACE_SCALE_KEYS = [
  "0",
  "100",
  "200",
  "300",
  "350",
  "400",
  "450",
  "500",
  "600",
] as const;

/**
 * Margin/padding props → their Tailwind class prefix and the CSS property (or
 * physical property pair, for the x/y axis shorthands) used for the inline
 * fallback when a value isn't a scale key.
 */
type SpaceProp =
  | { prefix: string; css: string; keywords?: Record<string, string> }
  | {
      prefix: string;
      axis: [string, string];
      keywords?: Record<string, string>;
    };

export const SPACE_PROPS: Record<string, SpaceProp> = {
  m: { prefix: "m", css: "margin", keywords: { auto: "auto" } },
  margin: { prefix: "m", css: "margin", keywords: { auto: "auto" } },
  mt: { prefix: "mt", css: "marginTop", keywords: { auto: "auto" } },
  mr: { prefix: "mr", css: "marginRight", keywords: { auto: "auto" } },
  mb: { prefix: "mb", css: "marginBottom", keywords: { auto: "auto" } },
  ml: { prefix: "ml", css: "marginLeft", keywords: { auto: "auto" } },
  mx: {
    prefix: "mx",
    axis: ["marginLeft", "marginRight"],
    keywords: { auto: "auto" },
  },
  my: {
    prefix: "my",
    axis: ["marginTop", "marginBottom"],
    keywords: { auto: "auto" },
  },
  p: { prefix: "p", css: "padding" },
  padding: { prefix: "p", css: "padding" },
  pt: { prefix: "pt", css: "paddingTop" },
  pr: { prefix: "pr", css: "paddingRight" },
  pb: { prefix: "pb", css: "paddingBottom" },
  pl: { prefix: "pl", css: "paddingLeft" },
  px: { prefix: "px", axis: ["paddingLeft", "paddingRight"] },
  py: { prefix: "py", axis: ["paddingTop", "paddingBottom"] },
};

/**
 * Single-property scale props: a scale-key value maps to `<prefix>-<key>`, any
 * other value falls back to an inline style on `css`. `keys` is the set of scale
 * keys with a shipped utility class.
 */
export interface ScaleProp {
  prefix: string;
  css: string;
  /** CSS custom-property namespace this scale resolves through (e.g. `radius`
   * → `var(--radius-500)`). */
  varPrefix: string;
  keys: readonly string[];
  companion?: { css: string; varPrefix: string };
}

/** Border radius scale (from @sproutsocial/seeds-border) → `rounded-*`. */
export const RADIUS_SCALE_KEYS = [
  "400",
  "500",
  "600",
  "800",
  "900",
  "1000",
  "pill",
  "inner",
  "outer",
] as const;

/** Border width scale (from @sproutsocial/seeds-border) → `border-w-*`. */
export const BORDER_WIDTH_SCALE_KEYS = ["500", "600", "700"] as const;

/** Font size scale (from @sproutsocial/seeds-typography) → `text-*`. */
export const FONT_SIZE_SCALE_KEYS = [
  "100",
  "200",
  "300",
  "400",
  "500",
  "600",
  "700",
] as const;

export const SCALE_PROPS: Record<string, ScaleProp> = {
  borderRadius: {
    prefix: "rounded",
    css: "borderRadius",
    varPrefix: "radius",
    keys: RADIUS_SCALE_KEYS,
  },
  borderWidth: {
    prefix: "border-w",
    css: "borderWidth",
    varPrefix: "border-width",
    keys: BORDER_WIDTH_SCALE_KEYS,
  },
  fontSize: {
    prefix: "text",
    css: "fontSize",
    varPrefix: "font-size",
    keys: FONT_SIZE_SCALE_KEYS,
    companion: { css: "lineHeight", varPrefix: "line-height" },
  },
  // Gap uses the space scale (Tailwind `gap-*` map to `--spacing-*`; here the
  // shipped Seeds `--space-*` vars).
  gap: {
    prefix: "gap",
    css: "gap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
  columnGap: {
    prefix: "gap-x",
    css: "columnGap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
  rowGap: {
    prefix: "gap-y",
    css: "rowGap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
  gridGap: {
    prefix: "gap",
    css: "gridGap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
  gridColumnGap: {
    prefix: "gap-x",
    css: "gridColumnGap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
  gridRowGap: {
    prefix: "gap-y",
    css: "gridRowGap",
    varPrefix: "space",
    keys: SPACE_SCALE_KEYS,
  },
};

/**
 * Layout size props. No token scale is wired for sizes, so these are always
 * inline. `fraction: true` reproduces styled-system's rule where a number in
 * (0, 1] becomes a percentage (e.g. `width={1}` → `100%`).
 */
export interface LayoutProp {
  css: string;
  classPrefix: string;
  fraction?: boolean;
  keywords?: Record<string, string>;
}

const SIZE_KEYWORDS = {
  auto: "auto",
  "min-content": "min",
  "max-content": "max",
  "fit-content": "fit",
};

const MAX_SIZE_KEYWORDS = {
  none: "none",
  "min-content": "min",
  "max-content": "max",
  "fit-content": "fit",
};

const MIN_SIZE_KEYWORDS = {
  "min-content": "min",
  "max-content": "max",
  "fit-content": "fit",
};

export const LAYOUT_PROPS: Record<string, LayoutProp> = {
  width: {
    css: "width",
    classPrefix: "w",
    fraction: true,
    keywords: SIZE_KEYWORDS,
  },
  height: { css: "height", classPrefix: "h", keywords: SIZE_KEYWORDS },
  minWidth: {
    css: "minWidth",
    classPrefix: "min-w",
    keywords: MIN_SIZE_KEYWORDS,
  },
  maxWidth: {
    css: "maxWidth",
    classPrefix: "max-w",
    keywords: MAX_SIZE_KEYWORDS,
  },
  minHeight: {
    css: "minHeight",
    classPrefix: "min-h",
    keywords: MIN_SIZE_KEYWORDS,
  },
  maxHeight: {
    css: "maxHeight",
    classPrefix: "max-h",
    keywords: MAX_SIZE_KEYWORDS,
  },
};

/**
 * Styled-system props with no class mapping (yet) — consumed and inlined as-is
 * so they never leak onto the DOM as invalid attributes. Class support can be
 * added incrementally; until then this keeps the shim behavior-complete.
 */
export const INLINE_ONLY_SYSTEM_PROPS = [
  "order",
  "border",
  "borderTop",
  "borderRight",
  "borderBottom",
  "borderLeft",
  "fontFamily",
  "gridColumn",
  "gridRow",
  "gridArea",
  "gridTemplateAreas",
] as const;

/**
 * Enum props: keyword value → its Tailwind utility class. An unknown keyword
 * falls back to an inline style on `css`.
 */
export interface EnumProp {
  css: string;
  map: Record<string, string>;
}

export const ENUM_PROPS: Record<string, EnumProp> = {
  display: {
    css: "display",
    map: {
      block: "block",
      "inline-block": "inline-block",
      inline: "inline",
      flex: "flex",
      "inline-flex": "inline-flex",
      grid: "grid",
      "inline-grid": "inline-grid",
      contents: "contents",
      "flow-root": "flow-root",
      table: "table",
      none: "hidden",
    },
  },
  overflow: {
    css: "overflow",
    map: {
      auto: "overflow-auto",
      hidden: "overflow-hidden",
      visible: "overflow-visible",
      scroll: "overflow-scroll",
      clip: "overflow-clip",
    },
  },
  overflowX: {
    css: "overflowX",
    map: {
      auto: "overflow-x-auto",
      hidden: "overflow-x-hidden",
      visible: "overflow-x-visible",
      scroll: "overflow-x-scroll",
      clip: "overflow-x-clip",
    },
  },
  overflowY: {
    css: "overflowY",
    map: {
      auto: "overflow-y-auto",
      hidden: "overflow-y-hidden",
      visible: "overflow-y-visible",
      scroll: "overflow-y-scroll",
      clip: "overflow-y-clip",
    },
  },
  textAlign: {
    css: "textAlign",
    map: {
      left: "text-left",
      center: "text-center",
      right: "text-right",
      justify: "text-justify",
      start: "text-start",
      end: "text-end",
    },
  },
  position: {
    css: "position",
    map: {
      static: "static",
      relative: "relative",
      absolute: "absolute",
      fixed: "fixed",
      sticky: "sticky",
    },
  },
  flexDirection: {
    css: "flexDirection",
    map: {
      row: "flex-row",
      "row-reverse": "flex-row-reverse",
      column: "flex-col",
      "column-reverse": "flex-col-reverse",
    },
  },
  flexWrap: {
    css: "flexWrap",
    map: {
      wrap: "flex-wrap",
      nowrap: "flex-nowrap",
      "wrap-reverse": "flex-wrap-reverse",
    },
  },
  alignItems: {
    css: "alignItems",
    map: {
      "flex-start": "items-start",
      start: "items-start",
      "flex-end": "items-end",
      end: "items-end",
      center: "items-center",
      baseline: "items-baseline",
      stretch: "items-stretch",
    },
  },
  justifyContent: {
    css: "justifyContent",
    map: {
      "flex-start": "justify-start",
      start: "justify-start",
      "flex-end": "justify-end",
      end: "justify-end",
      center: "justify-center",
      "space-between": "justify-between",
      "space-around": "justify-around",
      "space-evenly": "justify-evenly",
      stretch: "justify-stretch",
    },
  },
  alignSelf: {
    css: "alignSelf",
    map: {
      auto: "self-auto",
      "flex-start": "self-start",
      "flex-end": "self-end",
      center: "self-center",
      stretch: "self-stretch",
      baseline: "self-baseline",
    },
  },
  zIndex: { css: "zIndex", map: { auto: "z-auto" } },
  top: { css: "top", map: { auto: "top-auto" } },
  right: { css: "right", map: { auto: "right-auto" } },
  bottom: { css: "bottom", map: { auto: "bottom-auto" } },
  left: { css: "left", map: { auto: "left-auto" } },
  flexBasis: { css: "flexBasis", map: { auto: "basis-auto" } },
  flexGrow: { css: "flexGrow", map: { "0": "grow-0", "1": "grow" } },
  flexShrink: { css: "flexShrink", map: { "0": "shrink-0", "1": "shrink" } },
  flex: {
    css: "flex",
    map: {
      "1": "flex-1",
      "1 1 auto": "flex-auto",
      "0 1 auto": "flex-initial",
      none: "flex-none",
    },
  },
  fontStyle: {
    css: "fontStyle",
    map: { italic: "italic", normal: "not-italic" },
  },
  verticalAlign: {
    css: "verticalAlign",
    map: {
      baseline: "align-baseline",
      top: "align-top",
      middle: "align-middle",
      bottom: "align-bottom",
      "text-top": "align-text-top",
      "text-bottom": "align-text-bottom",
      sub: "align-sub",
      super: "align-super",
    },
  },
  cursor: {
    css: "cursor",
    map: {
      auto: "cursor-auto",
      default: "cursor-default",
      pointer: "cursor-pointer",
      wait: "cursor-wait",
      text: "cursor-text",
      move: "cursor-move",
      help: "cursor-help",
      "not-allowed": "cursor-not-allowed",
      none: "cursor-none",
      "context-menu": "cursor-context-menu",
      progress: "cursor-progress",
      cell: "cursor-cell",
      crosshair: "cursor-crosshair",
      "vertical-text": "cursor-vertical-text",
      alias: "cursor-alias",
      copy: "cursor-copy",
      "no-drop": "cursor-no-drop",
      grab: "cursor-grab",
      grabbing: "cursor-grabbing",
      "all-scroll": "cursor-all-scroll",
      "col-resize": "cursor-col-resize",
      "row-resize": "cursor-row-resize",
      "n-resize": "cursor-n-resize",
      "e-resize": "cursor-e-resize",
      "s-resize": "cursor-s-resize",
      "w-resize": "cursor-w-resize",
      "ne-resize": "cursor-ne-resize",
      "nw-resize": "cursor-nw-resize",
      "se-resize": "cursor-se-resize",
      "sw-resize": "cursor-sw-resize",
      "ew-resize": "cursor-ew-resize",
      "ns-resize": "cursor-ns-resize",
      "nesw-resize": "cursor-nesw-resize",
      "nwse-resize": "cursor-nwse-resize",
      "zoom-in": "cursor-zoom-in",
      "zoom-out": "cursor-zoom-out",
    },
  },
  whiteSpace: {
    css: "whiteSpace",
    map: {
      normal: "whitespace-normal",
      nowrap: "whitespace-nowrap",
      pre: "whitespace-pre",
      "pre-line": "whitespace-pre-line",
      "pre-wrap": "whitespace-pre-wrap",
      "break-spaces": "whitespace-break-spaces",
    },
  },
  borderStyle: {
    css: "borderStyle",
    map: {
      solid: "border-solid",
      dashed: "border-dashed",
      dotted: "border-dotted",
      double: "border-double",
      hidden: "border-hidden",
      none: "border-none",
    },
  },
  alignContent: {
    css: "alignContent",
    map: {
      normal: "content-normal",
      center: "content-center",
      "flex-start": "content-start",
      start: "content-start",
      "flex-end": "content-end",
      end: "content-end",
      "space-between": "content-between",
      "space-around": "content-around",
      "space-evenly": "content-evenly",
      baseline: "content-baseline",
      stretch: "content-stretch",
    },
  },
  justifyItems: {
    css: "justifyItems",
    map: {
      start: "justify-items-start",
      end: "justify-items-end",
      center: "justify-items-center",
      stretch: "justify-items-stretch",
      normal: "justify-items-normal",
    },
  },
  justifySelf: {
    css: "justifySelf",
    map: {
      auto: "justify-self-auto",
      start: "justify-self-start",
      end: "justify-self-end",
      center: "justify-self-center",
      stretch: "justify-self-stretch",
    },
  },
  fontWeight: {
    css: "fontWeight",
    map: {
      normal: "font-normal",
      semibold: "font-semibold",
      bold: "font-bold",
      extrabold: "font-extrabold",
    },
  },
  lineHeight: { css: "lineHeight", map: { normal: "leading-normal" } },
  letterSpacing: { css: "letterSpacing", map: { normal: "tracking-normal" } },
  boxShadow: { css: "boxShadow", map: { none: "shadow-none" } },
  gridAutoFlow: {
    css: "gridAutoFlow",
    map: {
      row: "grid-flow-row",
      column: "grid-flow-col",
      dense: "grid-flow-dense",
      "row dense": "grid-flow-row-dense",
      "column dense": "grid-flow-col-dense",
    },
  },
  gridAutoRows: {
    css: "gridAutoRows",
    map: {
      auto: "auto-rows-auto",
      "min-content": "auto-rows-min",
      "max-content": "auto-rows-max",
    },
  },
  gridAutoColumns: {
    css: "gridAutoColumns",
    map: {
      auto: "auto-cols-auto",
      "min-content": "auto-cols-min",
      "max-content": "auto-cols-max",
    },
  },
  gridTemplateRows: {
    css: "gridTemplateRows",
    map: { none: "grid-rows-none" },
  },
  gridTemplateColumns: {
    css: "gridTemplateColumns",
    map: { none: "grid-cols-none" },
  },
};

/**
 * Color props: styled-system color path → its Tailwind color utility. `prefix`
 * is the utility prefix; `css` is the CSS property for the inline fallback.
 */
export interface ColorProp {
  prefix: string;
  css: string;
  keywords?: Record<string, string>;
}

const COLOR_KEYWORDS: Record<string, string> = {
  inherit: "inherit",
  currentColor: "current",
  transparent: "transparent",
};

export const COLOR_PROPS: Record<string, ColorProp> = {
  color: { prefix: "text", css: "color", keywords: COLOR_KEYWORDS },
  bg: { prefix: "bg", css: "backgroundColor", keywords: COLOR_KEYWORDS },
  backgroundColor: {
    prefix: "bg",
    css: "backgroundColor",
    keywords: COLOR_KEYWORDS,
  },
  borderColor: {
    prefix: "border",
    css: "borderColor",
    keywords: COLOR_KEYWORDS,
  },
};

/**
 * Semantic color token paths (styled-system dotted path → Tailwind token
 * fragment). Note the `background` → `bg` abbreviation in the token names.
 * Seeded from the tokens the theme preset already backs (buildTailwindV4.ts).
 */
export const SEMANTIC_COLOR_TOKENS: Record<string, string> = {
  "text.body": "text-body",
  "text.headline": "text-headline",
  "text.subtext": "text-subtext",
  "app.background.base": "app-bg-base",
  "container.background.base": "container-bg-base",
  "container.background.success": "container-bg-success",
  "container.background.warning": "container-bg-warning",
  "container.background.error": "container-bg-error",
  "container.background.info": "container-bg-info",
  "container.background.opportunity": "container-bg-opportunity",
  "container.border.base": "container-border-base",
  "container.border.success": "container-border-success",
  "container.border.warning": "container-border-warning",
  "container.border.error": "container-border-error",
  "container.border.info": "container-border-info",
  "container.border.opportunity": "container-border-opportunity",
  "form.background.base": "form-bg-base",
  "form.border.base": "form-border-base",
  "form.border.selected": "form-border-selected",
  "form.placeholder.base": "form-placeholder-base",
};

/** Every styled-system prop the resolver consumes (so none reach the DOM). */
export const SYSTEM_PROP_NAMES: ReadonlySet<string> = new Set<string>([
  ...Object.keys(SPACE_PROPS),
  ...Object.keys(SCALE_PROPS),
  ...Object.keys(ENUM_PROPS),
  ...Object.keys(COLOR_PROPS),
  ...Object.keys(LAYOUT_PROPS),
  ...INLINE_ONLY_SYSTEM_PROPS,
  TYPE_SCALE_PROP_NAME,
]);
